<template>
    <div>
        <ts-page-title
            :title="$t('driverPerformance.pageTitle')"
            :breadcrumb="[
                { text: $t('home'), href: '/' },
                {
                    text: $t('driverPerformance.pageTitle'),
                    active: true
                }
            ]"
        />

        <ts-panel class="driver-performance-panel">
            <ts-loading-banner :loading="loading">
                <div class="tw-grid tw-grid-cols-1 tw-gap-3 tw-bg-gray-50 tw-p-3 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-6">
                    <div
                        v-for="card in summaryCards"
                        :key="card.key"
                        class="tw-rounded-xl tw-border tw-border-gray-100 tw-bg-white tw-p-3 tw-shadow-sm"
                    >
                        <div class="tw-flex tw-items-start tw-justify-between tw-gap-3">
                            <div class="tw-min-w-0">
                                <div class="tw-truncate tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-gray-600">
                                    {{ card.label }}
                                </div>
                                <div class="tw-mt-3 tw-flex tw-items-end tw-gap-1">
                                    <span
                                        class="tw-text-xl tw-font-bold tw-leading-none"
                                        :style="{ color: card.valueColor }"
                                    >
                                        {{ numberFormat(card.value) }}
                                    </span>
                                    <span
                                        v-if="card.suffix"
                                        class="tw-text-xs tw-font-semibold tw-text-gray-500"
                                    >
                                        {{ card.suffix }}
                                    </span>
                                    <span
                                        v-if="card.showPercentage"
                                        class="tw-text-xs tw-font-semibold"
                                        :style="{ color: card.valueColor }"
                                    >
                                        {{ summaryPercentage(card.value) }}%
                                    </span>
                                </div>
                            </div>
                            <div
                                class="tw-flex tw-h-8 tw-w-8 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg"
                                :style="{ backgroundColor: card.iconBackground, color: card.valueColor }"
                            >
                                <i :class="card.icon" />
                            </div>
                        </div>
                        <div class="tw-mt-3 tw-h-1.5 tw-overflow-hidden tw-rounded-full tw-bg-blue-50">
                            <div
                                class="tw-h-full tw-rounded-full tw-transition-all tw-duration-500"
                                :style="{ width: card.progressWidth, backgroundColor: card.color }"
                            />
                        </div>
                    </div>
                </div>

                <ts-panel-wrapper class="driver-performance-filter tw-pb-3">
                    <div
                        class="tw-grid tw-grid-cols-1 tw-items-end tw-gap-3 lg:tw-grid-cols-12"
                    >
                        <div class="lg:tw-col-span-4">
                            <label
                                class="required tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-gray-600"
                            >
                                {{ $t("driverPerformance.dateRange") }}
                            </label>
                            <a-range-picker
                                v-model="dateRange"
                                :allowClear="false"
                                format="DD-MM-YYYY"
                                valueFormat="DD-MM-YYYY"
                                style="width: 100%"
                            />
                        </div>

                        <div class="lg:tw-col-span-4">
                            <label
                                class="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-gray-600"
                            >
                                {{ $t("driverPerformance.driverName") }}
                            </label>
                            <a-select
                                show-search
                                allow-clear
                                v-model="driverIds"
                                mode="multiple"
                                style="width: 100%"
                                :default-active-first-option="false"
                                :show-arrow="false"
                                :max-tag-count="1"
                                :max-tag-text-length="18"
                                :filter-option="false"
                                :not-found-content="null"
                                :loading="driverSearchLoading"
                                :placeholder="
                                    $t('driverPerformance.typeToSearch')
                                "
                                @search="handleDriverSearch"
                            >
                                <a-select-option
                                    v-for="driver in drivers"
                                    :key="driver.driver_id"
                                >
                                    {{ driver.driver_code }} | {{ driver.driver_name }}
                                </a-select-option>
                            </a-select>
                        </div>

                        <div
                            class="tw-flex tw-h-8 tw-items-stretch tw-gap-2 lg:tw-col-span-4"
                        >
                            <div
                                class="tw-flex tw-h-full tw-min-w-0 tw-flex-1 tw-overflow-hidden tw-rounded-md tw-bg-gray-100 tw-p-0.5"
                            >
                                <button
                                    v-for="preset in datePresets"
                                    :key="preset.key"
                                    type="button"
                                    class="tw-flex-1 tw-whitespace-nowrap tw-rounded tw-px-2 tw-text-xs tw-font-medium tw-transition-colors"
                                    :class="
                                        isRangePreset(preset.key)
                                            ? 'tw-bg-white tw-text-blue-600 tw-shadow-sm'
                                            : 'tw-text-gray-600 hover:tw-bg-white hover:tw-text-gray-800'
                                    "
                                    @click="applyRangePreset(preset.key)"
                                >
                                    {{ preset.label }}
                                </button>
                            </div>

                            <ts-button
                                class="tw-flex tw-h-full tw-flex-shrink-0 tw-items-center tw-justify-center"
                                color="primary"
                                @click.prevent="fetchData({ page: 1 })"
                            >
                                <i class="fas fa-eye tw-mr-1" />
                                {{ $t("preview") }}
                            </ts-button>
                        </div>
                    </div>
                </ts-panel-wrapper>

                <div
                    class="tw-flex tw-flex-col tw-gap-2 tw-border-t tw-border-b tw-border-gray-100 tw-bg-white tw-px-4 tw-py-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between"
                >
                    <div class="tw-flex tw-items-center tw-gap-2">
                        <span
                            class="tw-text-xs tw-font-semibold tw-text-gray-700"
                        >
                            {{ $t("driverPerformance.deliveryBreakdown") }}
                        </span>
                        <span class="tw-h-3 tw-w-px tw-bg-gray-300" />
                        <span class="tw-text-xs tw-text-gray-400">
                            {{ $t("driverPerformance.valuesStatusRatio") }}
                        </span>
                    </div>
                    <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-4">
                        <div
                            v-for="status in statusLegend"
                            :key="status.key"
                            class="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-gray-600"
                        >
                            <span
                                class="tw-h-2 tw-w-2 tw-rounded-full"
                                :style="{ backgroundColor: status.color }"
                            />
                            {{ status.label }}
                        </div>
                    </div>
                </div>

                <div class="tw-pb-4">
                    <div v-if="orderedResources.length">
                        <article
                            v-for="(record, index) in orderedResources"
                            :key="record.driver_id"
                            class="tw-flex tw-flex-col tw-gap-2 tw-border-b tw-border-gray-100 tw-bg-white tw-px-4 tw-py-3 dark:tw-bg-gray-800 lg:tw-flex-row lg:tw-items-center lg:tw-gap-3"
                        >
                            <div
                                class="tw-flex tw-items-center tw-gap-2 lg:tw-w-48 lg:tw-flex-shrink-0"
                            >
                                <div
                                    class="tw-flex tw-h-6 tw-w-6 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-blue-600 tw-text-xs tw-font-bold tw-text-white"
                                >
                                    {{ recordNumber(index) }}
                                </div>
                                <div class="tw-min-w-0 tw-flex-1">
                                    <div class="tw-flex tw-min-w-0 tw-items-center tw-gap-1">
                                        <h3
                                            class="tw-mb-0 tw-truncate tw-text-xs tw-font-semibold tw-leading-tight tw-text-gray-800 dark:tw-text-white"
                                            :title="record.driver_name"
                                        >
                                            {{ record.driver_name }}
                                        </h3>
                                        <span
                                            class="tw-flex-shrink-0 tw-text-xs tw-font-semibold tw-text-gray-300"
                                        >
                                            #{{ paddedRecordNumber(index) }}
                                        </span>
                                    </div>
                                    <div
                                        class="tw-truncate tw-text-xs tw-font-normal tw-leading-tight tw-text-gray-500 dark:tw-text-gray-400"
                                    >
                                        {{ record.driver_code }}
                                    </div>
                                </div>
                            </div>

                            <div
                                class="tw-flex tw-items-center tw-justify-center tw-gap-1 tw-rounded-md tw-border tw-border-blue-100 tw-bg-blue-50 tw-px-3 tw-py-1.5 dark:tw-border-blue-800 dark:tw-bg-gray-700 lg:tw-w-20 lg:tw-flex-shrink-0"
                            >
                                <div
                                    class="tw-whitespace-nowrap tw-text-xs tw-font-bold tw-uppercase tw-leading-none tw-text-blue-500 dark:tw-text-blue-300"
                                >
                                    PKGS
                                </div>
                                <div
                                    class="tw-text-xs tw-font-bold tw-leading-none tw-text-blue-600 dark:tw-text-blue-400"
                                >
                                    {{ record.total }}
                                </div>
                            </div>

                            <div
                                class="tw-grid tw-flex-1 tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2 xl:tw-grid-cols-5"
                            >
                                <div
                                    v-for="status in statusBars(record)"
                                    :key="status.key"
                                    class="tw-rounded-md tw-px-2 tw-py-1"
                                    :class="{ 'tw-opacity-40': status.value === 0 }"
                                    :style="statusCardStyle(status)"
                                >
                                    <div
                                        class="tw-flex tw-items-center tw-justify-between tw-gap-1"
                                    >
                                        <div
                                            class="tw-flex tw-min-w-0 tw-items-center tw-gap-1"
                                        >
                                            <span
                                                class="tw-h-1.5 tw-w-1.5 tw-flex-shrink-0 tw-rounded-full"
                                                :style="{
                                                    backgroundColor:
                                                        status.color
                                                }"
                                            />
                                            <span
                                                class="tw-truncate tw-text-xs tw-font-semibold tw-text-gray-600"
                                            >
                                                {{ status.label }}
                                            </span>
                                        </div>
                                        <span
                                            class="tw-flex-shrink-0 tw-text-xs tw-font-semibold"
                                            :style="{
                                                color:
                                                    status.value > 0
                                                        ? status.textColor
                                                        : '#94a3b8'
                                            }"
                                        >
                                            {{
                                                statusPercentage(
                                                    record,
                                                    status.value
                                                )
                                            }}%
                                        </span>
                                    </div>
                                    <div
                                        class="tw-my-1 tw-text-xs tw-font-bold tw-leading-none tw-text-gray-800"
                                    >
                                        {{ status.value }}
                                    </div>
                                    <div
                                        class="tw-h-1 tw-overflow-hidden tw-rounded-full"
                                        :style="{
                                            backgroundColor:
                                                status.value > 0
                                                    ? status.trackColor
                                                    : '#e2e8f0'
                                        }"
                                        role="progressbar"
                                        :aria-label="status.label"
                                        :aria-valuenow="status.value"
                                        aria-valuemin="0"
                                        :aria-valuemax="
                                            Number(record.total) || 0
                                        "
                                    >
                                        <div
                                            class="tw-h-full tw-rounded-full tw-transition-all tw-duration-500"
                                            :style="{
                                                width: barWidth(
                                                    record,
                                                    status.value
                                                ),
                                                backgroundColor: status.color
                                            }"
                                        />
                                    </div>
                                </div>
                            </div>
                        </article>
                    </div>

                    <div
                        v-else
                        class="tw-mx-4 tw-mt-4 tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-dashed tw-border-gray-300 tw-bg-gray-50 tw-px-6 tw-py-8 dark:tw-border-gray-600 dark:tw-bg-gray-800"
                        :class="{ 'tw-invisible': loading }"
                        style="min-height: 240px"
                    >
                        <a-empty
                            :description="$t('driverPerformance.noRecords')"
                        />
                        <p
                            class="tw-mb-0 tw-mt-2 tw-max-w-md tw-text-center tw-text-sm tw-text-gray-500 dark:tw-text-gray-400"
                        >
                            {{ $t("driverPerformance.emptyHint") }}
                        </p>
                    </div>

                    <div
                        v-if="orderedResources.length"
                        class="tw-flex tw-flex-col tw-gap-3 tw-border-t tw-border-gray-100 tw-bg-white tw-px-4 tw-py-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between"
                    >
                        <div class="tw-text-xs tw-text-gray-500">
                            {{ paginationSummary }}
                        </div>

                        <ts-pagination
                            v-model="pagination"
                            @navigate="fetchData"
                        />
                    </div>
                </div>
            </ts-loading-banner>
        </ts-panel>
    </div>
</template>

<script>
import { debounce } from "lodash";
import moment from "moment";
import { mapActions, mapState } from "vuex";

export default {
    name: "driverPerformance",
    data() {
        return {
            loading: false,
            driverSearchLoading: false,
            drivers: []
        };
    },
    computed: {
        ...mapState("report/driverPerformance", ["resources", "pagination", "summary"]),
        dateRange: {
            get() {
                return this.$store.state.report.driverPerformance.dateRange;
            },
            set(value) {
                this.$store.commit(
                    "report/driverPerformance/SET_DATE_RANGE",
                    value
                );
            }
        },
        driverIds: {
            get() {
                return this.$store.state.report.driverPerformance.driverIds;
            },
            set(value) {
                this.$store.commit(
                    "report/driverPerformance/SET_DRIVER_IDS",
                    value
                );
            }
        },
        orderedResources() {
            return [...this.resources].sort(
                (first, second) => Number(second.total) - Number(first.total)
            );
        },
        datePresets() {
            return [
                {
                    key: "today",
                    label: this.$t("driverPerformance.today")
                },
                {
                    key: "last7Days",
                    label: this.$t("driverPerformance.last7Days")
                },
                {
                    key: "thisMonth",
                    label: this.$t("driverPerformance.thisMonth")
                }
            ];
        },
        statusLegend() {
            return this.statusBars({});
        },
        summaryCards() {
            const total = Number(this.summary.total) || 0;
            const statusCards = this.statusBars(this.summary).map(status => ({
                key: status.key,
                label: status.label,
                value: status.value,
                valueColor: status.textColor,
                color: status.color,
                iconBackground: status.backgroundColor,
                icon: this.summaryIcon(status.key),
                showPercentage: true,
                progressWidth: total ? this.summaryBarWidth(status.value) : "0%"
            }));

            return [
                {
                    key: "total",
                    label: this.$t("driverPerformance.totalDispatched"),
                    value: total,
                    suffix: this.$t("driverPerformance.pkgs"),
                    valueColor: "#0f172a",
                    color: "#2563eb",
                    iconBackground: "#dbeafe",
                    icon: "fas fa-truck",
                    showPercentage: false,
                    progressWidth: total ? "100%" : "0%"
                },
                ...statusCards
            ];
        },
        paginationSummary() {
            const total = Number(this.pagination.total) || 0;

            if (total === 0) {
                return "Showing 0 entries";
            }

            const from = Number(this.pagination.from) || 1;
            const to = Math.min(Number(this.pagination.to) || from, total);

            return "Showing " + from + " to " + to + " of " + total + " entries";
        }
    },
    created() {
        this.handleDriverSearch();
        this.fetchData();
    },
    methods: {
        ...mapActions("report/driverPerformance", ["fetch"]),
        ...mapActions("report/formViewModel", ["fetchFormView"]),
        recordNumber(index) {
            return (
                (this.pagination.currentPage - 1) * this.pagination.perPage +
                index +
                1
            );
        },
        paddedRecordNumber(index) {
            return String(this.recordNumber(index)).padStart(2, "0");
        },
        statusBars(record) {
            return [
                {
                    key: "success",
                    label: this.$t("driverPerformance.success"),
                    value: Number(record.success) || 0,
                    color: "#10b981",
                    textColor: "#047857",
                    backgroundColor: "#f0fdf4",
                    borderColor: "#bbf7d0",
                    trackColor: "#d1fae5"
                },
                {
                    key: "pending",
                    label: this.$t("driverPerformance.pending"),
                    value: Number(record.pending) || 0,
                    color: "#f59e0b",
                    textColor: "#b45309",
                    backgroundColor: "#fffbeb",
                    borderColor: "#fde68a",
                    trackColor: "#fef3c7"
                },
                {
                    key: "on_delivery",
                    label: this.$t("driverPerformance.onDelivery"),
                    value: Number(record.on_delivery) || 0,
                    color: "#3b82f6",
                    textColor: "#1d4ed8",
                    backgroundColor: "#eff6ff",
                    borderColor: "#bfdbfe",
                    trackColor: "#dbeafe"
                },
                {
                    key: "cancelled",
                    label: this.$t("driverPerformance.cancelled"),
                    value: Number(record.cancelled) || 0,
                    color: "#f43f5e",
                    textColor: "#be123c",
                    backgroundColor: "#fff1f2",
                    borderColor: "#fecdd3",
                    trackColor: "#ffe4e6"
                },
                {
                    key: "return",
                    label: this.$t("driverPerformance.return"),
                    value: Number(record.return) || 0,
                    color: "#8b5cf6",
                    textColor: "#6d28d9",
                    backgroundColor: "#faf5ff",
                    borderColor: "#e9d5ff",
                    trackColor: "#ede9fe"
                }
            ];
        },
        statusCardStyle() {
            return {
                backgroundColor: "#ffffff",
                borderColor: "transparent"
            };
        },
        presetRange(preset) {
            const endDate = moment();
            let startDate = endDate.clone();

            if (preset === "last7Days") {
                startDate = endDate.clone().subtract(6, "days");
            }

            if (preset === "thisMonth") {
                startDate = endDate.clone().startOf("month");
            }

            return [
                startDate.format("DD-MM-YYYY"),
                endDate.format("DD-MM-YYYY")
            ];
        },
        isRangePreset(preset) {
            const range = this.presetRange(preset);

            return (
                this.dateRange &&
                this.dateRange[0] === range[0] &&
                this.dateRange[1] === range[1]
            );
        },
        applyRangePreset(preset) {
            this.dateRange = this.presetRange(preset);
        },
        numberFormat(value) {
            return new Intl.NumberFormat().format(Number(value) || 0);
        },
        summaryPercentage(value) {
            const total = Number(this.summary.total) || 0;

            if (total === 0) {
                return "0.0";
            }

            return ((Number(value) || 0) / total * 100).toFixed(1);
        },
        summaryBarWidth(value) {
            const percentage = Number(this.summaryPercentage(value));

            if (percentage === 0) {
                return "0%";
            }

            return Math.max(percentage, 3) + "%";
        },
        summaryIcon(key) {
            return {
                success: "far fa-check-circle",
                pending: "fas fa-hourglass-half",
                on_delivery: "fas fa-route",
                cancelled: "far fa-times-circle",
                return: "far fa-window-restore"
            }[key] || "fas fa-box";
        },
        barWidth(record, value) {
            const percentage = this.statusPercentage(record, value);

            if (percentage === 0) {
                return "0%";
            }

            return `${Math.max(percentage, 3)}%`;
        },
        statusPercentage(record, value) {
            const total = Number(record.total) || 0;

            if (total === 0) {
                return 0;
            }

            const percentage = (value / total) * 100;

            return Number.isInteger(percentage)
                ? percentage
                : Number(percentage.toFixed(1));
        },
        fetchData(attributes = {}) {
            this.loading = true;
            this.fetch(attributes)
                .catch(error => {
                    this.$notify({ type: "error", text: error.message });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        handleDriverSearch: debounce(function(value) {
            this.driverSearchLoading = true;

            this.fetchFormView({
                fnName: "driver",
                search_driver: value
            })
                .then(response => {
                    this.drivers = response.data.driver;
                })
                .catch(error => {
                    this.$notify({ type: "error", text: error.message });
                })
                .finally(() => {
                    this.driverSearchLoading = false;
                });
        }, 500)
    }
};
</script>

<style scoped lang="scss">
.driver-performance-panel {
    border: 1px solid #e5edf7;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}

.driver-performance-panel >>> .card,
.driver-performance-panel >>> .card-body {
    border: 0;
    border-radius: 12px;
}

.driver-performance-panel >>> .card-body {
    padding: 0;
}

.driver-performance-filter {
    background: #fff;
    padding: 14px 16px 16px;
}

.driver-performance-panel >>> .ant-calendar-picker-input,
.driver-performance-panel >>> .ant-select-selection {
    min-height: 34px;
    border-color: #d8e2ef;
    border-radius: 6px;
    color: #334155;
    font-size: 12px;
}

.driver-performance-panel >>> .ant-select-selection__placeholder,
.driver-performance-panel >>> .ant-select-search__field__placeholder {
    color: #94a3b8;
}

.driver-performance-panel >>> .pagination {
    margin-bottom: 0;
}

.driver-performance-panel >>> .pagination > .pagination {
    display: flex;
    align-items: center;
    gap: 6px;
}

.driver-performance-panel >>> .pagination .form-control {
    height: 28px;
    min-width: 48px;
    border-color: #d8e2ef;
    border-radius: 6px;
    color: #475569;
    font-size: 12px;
    padding: 2px 8px;
}

.driver-performance-panel >>> .pagination span {
    color: #475569;
    font-size: 12px;
}

.driver-performance-panel >>> .page-link {
    min-width: 28px;
    height: 28px;
    border-color: #e5edf7;
    border-radius: 8px;
    color: #64748b;
    font-size: 12px;
    line-height: 14px;
    padding: 6px 8px;
    text-align: center;
}

.driver-performance-panel >>> .page-item.active .page-link {
    background: #2563eb;
    border-color: #2563eb;
    color: #fff;
}

.driver-performance-panel >>> .page-item.previous .page-link,
.driver-performance-panel >>> .page-item.next .page-link {
    width: 28px;
    overflow: hidden;
    position: relative;
    text-indent: 999px;
}

.driver-performance-panel >>> .page-item.previous .page-link::after {
    content: "‹";
}

.driver-performance-panel >>> .page-item.next .page-link::after {
    content: "›";
}

.driver-performance-panel >>> .page-item.previous .page-link::after,
.driver-performance-panel >>> .page-item.next .page-link::after {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    text-indent: 0;
}
</style>
